import isEqual from 'lodash/isEqual';

import { ArticleFields, IArticle } from '@pressreader/content-types';
import type { RequiredKeys } from '@pressreader/types';
import { hasFlag } from '@pressreader/utils';

interface FieldMergeStrategy {
    /** ArticleFields flag to check in source.availableFields to see if that field is available.
     * If null, the field is always merged.
     */
    flag: ArticleFields | null;
    /** Whether to merge this field or not. If false, the field will not be merged.
     * @default true
     */
    merge?: false;
}
type ArticleMergeStrategy = Record<RequiredKeys<IArticle>, FieldMergeStrategy> & Partial<Record<keyof IArticle, FieldMergeStrategy>>;

/**
 * Maps fields of an article to their corresponding flags in ArticleFields.
 * Fields that do not have a corresponding flag are marked with null.
 * Fields that should not be merged are marked with merge: false.
 */
const mergeStrategy: ArticleMergeStrategy = {
    // Fields that have corresponding flags in ArticleFields
    access: { flag: ArticleFields.Access },
    analyticProfiles: { flag: ArticleFields.AnalyticProfiles },
    bestImage: { flag: ArticleFields.Images },
    classification: { flag: ArticleFields.Classification },
    comments: { flag: ArticleFields.Comments },
    coverImage: { flag: ArticleFields.Images },
    images: { flag: ArticleFields.Images },
    issue: { flag: ArticleFields.Issue },
    paragraphs: { flag: ArticleFields.Paragraphs },
    publication: { flag: ArticleFields.Publication },
    related: { flag: ArticleFields.Related },
    shortContent: { flag: ArticleFields.ShortContent },
    similars: { flag: ArticleFields.Similars },
    votes: { flag: ArticleFields.Votes },

    // Fields that do not have corresponding flags
    articleSource: { flag: null },
    author: { flag: null },
    date: { flag: null },
    id: { flag: null },
    language: { flag: null },
    originalLanguage: { flag: null },
    primaryArticleId: { flag: null },
    rank: { flag: null },
    rate: { flag: null },
    searchHighlights: { flag: null },
    subtitle: { flag: null },
    textLength: { flag: null },
    readingTimeMinutes: { flag: null },
    title: { flag: null },

    // Fields that should not be merged automatically
    availableFields: { flag: null, merge: false },
    availableTextLength: { flag: null, merge: false },
    restrictedFields: { flag: null, merge: false },
};

/** Mutates target if the source has a new value for a field.  */
function mergeField<T extends IArticle>(target: T, source: T, field: keyof T, flag?: ArticleFields | null): boolean {
    if (isEqual(target[field], source[field])) {
        return false;
    }

    if (!flag) {
        target[field] = source[field];
        return true;
    } else if (hasFlag(source.availableFields, flag)) {
        target[field] = source[field];
        target.availableFields |= flag;
        target.restrictedFields &= ~flag;
        return true;
    }
    return false;
}

/**
 * Merges two articles based on their availableFields flags.
 * Fields from the source article will be merged into the target article
 * only if the source article has that field available.
 *
 * @param targetArticle The article to merge data into
 * @param sourceArticle The article to take data from
 * @returns A new merged article with combined data and updated availableFields
 */
export function mergeArticlesByAvailableFields(targetArticle: IArticle, sourceArticle: IArticle): IArticle {
    const mergedArticle = { ...targetArticle };
    let articleUpdated = false;
    for (const [field, { flag }] of (Object.entries(mergeStrategy) as [keyof IArticle, FieldMergeStrategy][]).filter(
        ([, { merge }]) => merge !== false,
    )) {
        // Note: has to be done as a separate step to make sure mergeField is called.
        const changed = mergeField(mergedArticle, sourceArticle, field, flag);
        articleUpdated ||= changed;
    }

    // `summary` lives inside `classification` but is tracked by its own flag: a source carrying
    // `Classification` without `Summary` (e.g. the feed field set — the backend nulls the summary
    // it wasn't asked for) replaces the object with a summary-less copy while the mask OR below
    // keeps the target's `Summary` bit, so the data would vanish yet stay marked as loaded and no
    // later load would restore it. Carry the target's summary into the replacing copy instead.
    // (A target summary implies its `Summary` bit — the mapper sets them together.)
    if (
        !hasFlag(sourceArticle.availableFields, ArticleFields.Summary) &&
        mergedArticle.classification != null &&
        mergedArticle.classification.summary == null &&
        targetArticle.classification?.summary != null
    ) {
        mergedArticle.classification = { ...mergedArticle.classification, summary: targetArticle.classification.summary };
    }

    const mergedAvailableFields = targetArticle.availableFields | sourceArticle.availableFields;
    const mergedRestrictedFields = (targetArticle.restrictedFields | sourceArticle.restrictedFields) & ~mergedAvailableFields;
    if (mergedArticle.availableFields !== mergedAvailableFields || mergedArticle.restrictedFields !== mergedRestrictedFields) {
        mergedArticle.availableFields = mergedAvailableFields;
        mergedArticle.restrictedFields = mergedRestrictedFields;
        articleUpdated = true;
    }

    const targetMissingMetadata = targetArticle.missingMetadata ?? 0;
    const sourceMissingMetadata = sourceArticle.missingMetadata ?? 0;
    const mergedMissingMetadata = targetMissingMetadata | sourceMissingMetadata;
    if (mergedArticle.missingMetadata !== mergedMissingMetadata) {
        mergedArticle.missingMetadata = mergedMissingMetadata || undefined;
        articleUpdated = true;
    }

    // Update text length information if paragraphs were merged
    if (hasFlag(mergedArticle.availableFields, ArticleFields.Paragraphs) && mergedArticle.paragraphs.length > 0) {
        mergedArticle.availableTextLength = mergedArticle.textLength;
    }

    return articleUpdated ? mergedArticle : targetArticle;
}
